import { apiGet, apiPost } from '@/shared/utils/requestBuilder';
import { getStoredWorkspaceId, setStoredWorkspaceId } from '@/shared/utils/workspace';

import type {
  CancelNegociacaoStartResult,
  ClosePhoneConflictResult,
  CpfCnpjValidationData,
  CreateNegociacaoIndividualInput,
  CreateNegociacaoIndividualResult,
  Flow,
  ValidatePhoneConflictInput,
  ValidatePhoneConflictResult,
} from '../types';

import type {
  PublishedAgentPageQuery,
  PublishedAgentCatalogPage,
} from '../types/publishedAgentCatalog';

const flowsBaseUrl = '/negotiation-agents/published';

function extractErrorMessage(body: unknown, fallback: string): string {
  if (!body || typeof body !== 'object') {
    return fallback;
  }

  const typed = body as {
    error?: string | { message?: string };
    message?: string;
  };

  if (typeof typed.error === 'string' && typed.error.trim()) {
    return typed.error;
  }

  if (typed.error && typeof typed.error === 'object' && typeof typed.error.message === 'string') {
    return typed.error.message;
  }

  if (typeof typed.message === 'string' && typed.message.trim()) {
    return typed.message;
  }

  return fallback;
}

async function parseJsonOrThrow(response: Response, defaultMsg: string): Promise<unknown> {
  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: defaultMsg }));
    const errMsg = extractErrorMessage(body, defaultMsg);
    throw new Error(errMsg);
  }

  return (await response.json().catch(() => ({}))) as unknown;
}

function unwrapDataEnvelope<T>(json: unknown): T {
  const payload = (json as { data?: unknown })?.data ?? json;
  return payload as T;
}

function unwrapSuccessEnvelope<T>(json: unknown): T {
  const typed = json as
    | { success: true; data: T }
    | { success: false; error?: { message?: string } }
    | T;

  if (typeof typed === 'object' && typed && 'success' in typed) {
    if ((typed as { success: boolean }).success === true) {
      return (typed as { data: T }).data;
    }

    const msg = (typed as { error?: { message?: string } })?.error?.message;
    throw new Error(msg || 'Erro na requisição');
  }

  return typed as T;
}

async function ensureWorkspaceId(): Promise<string> {
  if (typeof window === 'undefined') {
    throw new Error('Workspace não definido');
  }

  const stored = getStoredWorkspaceId();
  if (stored) {
    return stored;
  }

  const response = await apiGet('/escritorios/pessoal');
  const json = await parseJsonOrThrow(response, 'Erro ao buscar workspace pessoal');
  const ws = unwrapDataEnvelope<{ id: string } | null>(json);
  const wsId = ws?.id ? String(ws.id) : null;

  if (!wsId) {
    throw new Error('Workspace não definido');
  }

  setStoredWorkspaceId(wsId);
  return wsId;
}

function assertCurrentWorkspace(workspaceId: string): void {
  if (getStoredWorkspaceId() !== workspaceId)
    throw new Error('Workspace alterado durante o carregamento dos agentes.');
}

export const negociacaoIndividualWizardService = {
  listAgents: async (query: PublishedAgentPageQuery): Promise<PublishedAgentCatalogPage> => {
    const workspaceId = await ensureWorkspaceId();
    const params = new URLSearchParams({ page: String(query.page), limit: String(query.limit) });
    if (query.search) params.set('search', query.search);
    const response = await apiGet(`${flowsBaseUrl}?${params.toString()}`);
    const json = await parseJsonOrThrow(response, 'Erro ao listar agentes');
    assertCurrentWorkspace(workspaceId);
    return unwrapDataEnvelope<PublishedAgentCatalogPage>(json);
  },

  getAgentById: async (flowId: string): Promise<Flow> => {
    const workspaceId = await ensureWorkspaceId();
    const response = await apiGet(`${flowsBaseUrl}/${flowId}`);
    const json = await parseJsonOrThrow(response, 'Erro ao carregar detalhes do agente');
    assertCurrentWorkspace(workspaceId);
    return unwrapDataEnvelope<Flow>(json);
  },

  validarCpfCnpj: async (documento: string): Promise<CpfCnpjValidationData> => {
    const response = await apiPost('/integracoes/cpf-cnpj/validar', { documento });
    const json = await parseJsonOrThrow(response, 'Erro ao validar CPF/CNPJ');
    return unwrapDataEnvelope<CpfCnpjValidationData>(json);
  },

  createNegociacaoIndividual: async (
    input: CreateNegociacaoIndividualInput,
  ): Promise<CreateNegociacaoIndividualResult> => {
    await ensureWorkspaceId();
    const response = await apiPost('/negociacao/casos/individual', input);
    const json = await parseJsonOrThrow(response, 'Erro ao criar negociação individual');
    return unwrapSuccessEnvelope<CreateNegociacaoIndividualResult>(json);
  },

  cancelStart: async (caseId: string): Promise<CancelNegociacaoStartResult> => {
    await ensureWorkspaceId();
    const response = await apiPost(`/negociacao/casos/${caseId}/cancel-start`, {});
    const json = await parseJsonOrThrow(response, 'Erro ao cancelar início agendado');
    return unwrapSuccessEnvelope<CancelNegociacaoStartResult>(json);
  },

  validatePhoneConflict: async (
    input: ValidatePhoneConflictInput,
  ): Promise<ValidatePhoneConflictResult> => {
    await ensureWorkspaceId();
    const response = await apiPost('/negociacao/casos/phone-conflict/validate', input);
    const json = await parseJsonOrThrow(response, 'Erro ao validar conflito por WhatsApp');
    return unwrapSuccessEnvelope<ValidatePhoneConflictResult>(json);
  },

  closePhoneConflict: async (caseId: string): Promise<ClosePhoneConflictResult> => {
    await ensureWorkspaceId();
    const response = await apiPost(`/negociacao/casos/phone-conflict/${caseId}/close`, {});
    const json = await parseJsonOrThrow(response, 'Erro ao encerrar negociação em conflito');
    return unwrapSuccessEnvelope<ClosePhoneConflictResult>(json);
  },
};
